import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router';
import { RotateCw } from 'lucide-react';
import {
  BarChart,
  Button,
  EmptyState,
  ErrorState,
  IconButton,
  MetricStrip,
  PageHeader,
  Section,
  Skeleton,
  StatusDot,
  cn,
} from '@zentera/ui';
import { useBreadcrumbs } from '../components/app-shell';
import { DemoTag, RelativeTime } from '../components/common';
import { api } from '../lib/api';
import { useCan } from '../lib/auth';
import { fmt } from '../lib/format';
import { auditLabel, stepLabel } from '../lib/labels';
import type { Alert, Overview } from '../lib/types';

export function OverviewPage() {
  useBreadcrumbs([{ label: 'Visão geral' }]);
  const q = useQuery({ queryKey: ['overview'], queryFn: () => api.get<Overview>('/api/overview'), refetchInterval: 60_000 });

  return (
    <div className="space-y-8">
      <PageHeader
        title="Visão geral"
        description="Estado operacional das instalações, consumo de IA e pendências da equipe."
        meta={q.dataUpdatedAt ? <span>Atualizado às {new Date(q.dataUpdatedAt).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}</span> : null}
        actions={<IconButton label="Atualizar" icon={<RotateCw className={cn(q.isFetching && 'animate-spin')} />} onClick={() => void q.refetch()} />}
      />
      {q.isError ? (
        <ErrorState error={q.error} onRetry={() => void q.refetch()} />
      ) : !q.data ? (
        <OverviewSkeleton />
      ) : q.data.providers.total === 0 ? (
        <EmptyState
          title="Nenhum provedor cadastrado"
          description="Cadastre o primeiro provedor para iniciar a implantação da instância dedicada."
          action={
            <Button variant="primary" asChild>
              <Link to="/providers/new">Cadastrar provedor</Link>
            </Button>
          }
        />
      ) : (
        <OverviewContent data={q.data} />
      )}
    </div>
  );
}

function OverviewSkeleton() {
  return (
    <div className="space-y-8">
      <Skeleton className="h-[86px] w-full rounded-lg" />
      <div className="grid gap-8 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        <Skeleton className="h-72 rounded-lg" />
        <Skeleton className="h-72 rounded-lg" />
      </div>
    </div>
  );
}

function OverviewContent({ data }: { data: Overview }) {
  const { providers, instances, usage, alerts, deployments, updates } = data;
  const deployed = instances.total - instances.provisioning;

  return (
    <>
      <MetricStrip
        metrics={[
          {
            label: 'Provedores',
            value: fmt.int(providers.total),
            detail: `${providers.active} ativos · ${providers.onboarding} em implantação`,
          },
          {
            label: 'Instâncias online',
            value: (
              <>
                {instances.online}
                <span className="text-base font-normal text-fg-4"> / {deployed}</span>
              </>
            ),
            detail:
              instances.offline + instances.degraded > 0
                ? `${instances.offline} offline · ${instances.degraded} degradadas`
                : 'Todas respondendo',
            tone: instances.offline > 0 ? 'danger' : instances.degraded > 0 ? 'warning' : undefined,
          },
          { label: 'Atendimentos · 30 dias', value: fmt.compact(usage.conversations30d), detail: `${fmt.compact(usage.requests30d)} requisições de IA` },
          { label: 'Custo de IA no mês', value: fmt.usdMicros(usage.costUsdMicrosMonth), detail: `${fmt.compact(usage.requestsMonth)} requisições` },
          {
            label: 'Alertas críticos',
            value: alerts.critical,
            detail: alerts.integrationFailures ? `${alerts.integrationFailures} ${alerts.integrationFailures === 1 ? 'falha' : 'falhas'} de integração` : 'Sem falhas de integração',
            tone: alerts.critical > 0 ? 'danger' : undefined,
          },
          {
            label: 'Atualizações pendentes',
            value: updates.outdated.length,
            detail: updates.latestVersion ? `Versão atual ${updates.latestVersion}` : 'Nenhuma versão registrada',
          },
        ]}
      />

      <div className="grid gap-x-10 gap-y-8 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        <div className="min-w-0 space-y-8">
          <AttentionList alerts={alerts.items} />
          <Section title="Atendimentos processados" description="Conversas iniciadas por dia, todas as instâncias · últimos 30 dias">
            {usage.daily.length === 0 ? (
              <div className="rounded-lg border border-line">
                <EmptyState compact title="Sem dados de consumo" description="As instâncias enviam métricas agregadas diariamente." />
              </div>
            ) : (
              <BarChart
                ariaLabel="Atendimentos por dia nos últimos 30 dias"
                data={usage.daily.map((d) => ({
                  key: d.day,
                  value: d.conversations,
                  label: fmt.dayShort(d.day),
                  tooltip: `${fmt.dayShort(d.day)} · ${fmt.int(d.conversations)} atendimentos · ${fmt.usdMicros(d.costUsdMicros)}`,
                }))}
                formatAxis={(v) => fmt.compact(v)}
              />
            )}
          </Section>
        </div>

        <div className="min-w-0 space-y-8">
          <Section title="Implantações pendentes" actions={<Link to="/providers" className="text-xs text-fg-3 hover:text-fg">Provedores</Link>}>
            {deployments.pending.length === 0 ? (
              <p className="text-sm text-fg-3">Nenhuma implantação em andamento.</p>
            ) : (
              <ul className="divide-y divide-line border-y border-line">
                {deployments.pending.map((d) => (
                  <li key={d.id}>
                    <Link to={`/providers/${d.id}/infrastructure`} className="group flex items-center gap-3 py-2.5">
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm text-fg group-hover:text-brand">{d.name}</div>
                        <div className="truncate text-xs text-fg-3">Próxima: {stepLabel(d.next_step)}</div>
                      </div>
                      <Progress done={d.done} total={d.total} />
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </Section>

          <Section title="Versões">
            {updates.outdated.length === 0 ? (
              <p className="text-sm text-fg-3">
                {updates.latestVersion ? `Todas as instâncias estão na versão ${updates.latestVersion}.` : 'Nenhuma versão da aplicação registrada ainda.'}
              </p>
            ) : (
              <ul className="divide-y divide-line border-y border-line">
                {updates.outdated.map((o) => (
                  <li key={o.id} className="flex items-center justify-between py-2.5 text-sm">
                    <Link to={`/infrastructure/${o.id}`} className="truncate text-fg hover:text-brand">{o.name}</Link>
                    <span className="font-mono text-xs text-fg-3">{o.version} → {updates.latestVersion}</span>
                  </li>
                ))}
              </ul>
            )}
          </Section>

          <Section title="Atividade recente" actions={<Link to="/security" className="text-xs text-fg-3 hover:text-fg">Auditoria</Link>}>
            <ul className="space-y-3">
              {data.recentAudit.map((a) => (
                <li key={a.id} className="text-sm">
                  <div className="text-fg">{auditLabel(a.action)}</div>
                  <div className="text-xs text-fg-3">
                    {a.actorLabel} · <RelativeTime value={a.at} />
                  </div>
                </li>
              ))}
              {data.recentAudit.length === 0 && <li className="text-sm text-fg-3">Sem eventos.</li>}
            </ul>
          </Section>
        </div>
      </div>
    </>
  );
}

function Progress({ done, total }: { done: number; total: number }) {
  return (
    <div className="flex shrink-0 items-center gap-2">
      <div className="flex gap-[2px]" aria-hidden="true">
        {Array.from({ length: total }, (_, i) => (
          <span key={i} className={cn('h-2.5 w-1 rounded-[1px]', i < done ? 'bg-fg-2' : 'bg-line-strong')} />
        ))}
      </div>
      <span className="tabular w-9 text-right text-xs text-fg-3">{done}/{total}</span>
    </div>
  );
}

const severityTone = { critical: 'danger', warning: 'warning', info: 'info' } as const;
const severityLabel = { critical: 'Crítico', warning: 'Atenção', info: 'Info' } as const;

function AttentionList({ alerts }: { alerts: Alert[] }) {
  const can = useCan();
  const qc = useQueryClient();
  const ack = useMutation({
    mutationFn: (id: string) => api.post(`/api/alerts/${id}/ack`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['overview'] }),
  });

  return (
    <Section title="Requer atenção" description="Alertas abertos, do mais grave para o mais recente">
      {alerts.length === 0 ? (
        <div className="rounded-lg border border-line">
          <EmptyState compact title="Nenhum alerta aberto" description="Instâncias, integrações e orçamentos dentro do esperado." />
        </div>
      ) : (
        <ul className="divide-y divide-line overflow-hidden rounded-lg border border-line">
          {alerts.map((a) => (
            <li key={a.id} className={cn('flex items-start gap-3 px-4 py-3', a.acknowledgedAt && 'bg-subtle/60')}>
              <StatusDot tone={severityTone[a.severity]} className="mt-[7px] shrink-0 text-xs sm:mt-0.5 sm:w-16">
                <span className="max-sm:sr-only">{severityLabel[a.severity]}</span>
              </StatusDot>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                  {a.providerId ? (
                    <Link to={`/providers/${a.providerId}`} className="text-sm text-fg hover:text-brand">{a.title}</Link>
                  ) : (
                    <span className="text-sm text-fg">{a.title}</span>
                  )}
                  {a.isDemo && <DemoTag />}
                </div>
                <div className="mt-0.5 truncate text-xs text-fg-3">
                  {a.providerName && <>{a.providerName} · </>}
                  {a.detail && <>{a.detail} · </>}
                  aberto <RelativeTime value={a.openedAt} />
                </div>
              </div>
              {a.acknowledgedAt ? (
                <span className="shrink-0 pt-0.5 text-xs text-fg-4">Reconhecido</span>
              ) : (
                can('alerts.ack') && (
                  <Button size="sm" variant="ghost" loading={ack.isPending && ack.variables === a.id} onClick={() => ack.mutate(a.id)}>
                    Reconhecer
                  </Button>
                )
              )}
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}
